import React from 'react';

import Chip, { ChipProps } from '@components/Chip';
import { useRequests } from '@context/RequestsContext';
import { chooseByStatus } from '@db/Requests/Common';
import { RequestMutationAction, useJoinPlanRequestMutation } from '@db/Requests/Queries';
import { errorAlert } from '@helpers/Alert';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { Plan } from '@types';
import { RequestStatus } from '@types';

export type PlanJoinButtonProps = {
  planDoc: Pick<Plan, 'planId' | 'userIds'>;
} & Omit<ChipProps, 'children'>;

export default function PlanJoinButton({ planDoc, ...props }: PlanJoinButtonProps) {
  const currentUserId = getCurrentUserId();
  const isUserJoinedInitially = (userIds?: PlanJoinButtonProps['planDoc']['userIds']) => {
    return (userIds || []).includes(currentUserId);
  };

  const isUserRequested = (requestIds: Set<string>, planId: string) => {
    return requestIds.has(planId);
  };

  const { pendingJoinPlanRequestPlanIds } = useRequests();

  const [loading, setLoading] = React.useState(false);
  const [joined, setJoined] = React.useState<boolean>(isUserJoinedInitially(planDoc.userIds));
  const [requested, setRequested] = React.useState<boolean>(
    isUserRequested(pendingJoinPlanRequestPlanIds, planDoc.planId)
  );
  const joinRequestMutation = useJoinPlanRequestMutation();

  React.useEffect(() => {
    if ((planDoc.userIds || []).includes(currentUserId)) {
      // Server/listener state can confirm a join after an optimistic local update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setJoined(true);
    }
  }, [currentUserId, planDoc.userIds]);

  React.useEffect(() => {
    if (isUserRequested(pendingJoinPlanRequestPlanIds, planDoc.planId)) {
      // Server/listener state can confirm a pending request after an optimistic local update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRequested(true);
    }
  }, [pendingJoinPlanRequestPlanIds, planDoc.planId]);

  const onPress = () => {
    setLoading(true);
    joinRequestMutation.mutate(
      {
        action: joined || requested ? RequestMutationAction.DELETE : RequestMutationAction.ADD,
        planId: planDoc.planId,
        userId: currentUserId,
      },
      {
        onSuccess: (status, { action }) => {
          if (action === RequestMutationAction.DELETE) {
            setJoined(false);
            setRequested(false);
            return;
          }

          if (status === RequestStatus.ACCEPTED) {
            setJoined(true);
          }
          if (status === RequestStatus.PENDING) {
            setRequested(true);
          }
        },
        onError: (error) => errorAlert(error, { onAction: onPress }),
        onSettled: () => setLoading(false),
      }
    );
  };

  const mutationLoading = loading || joinRequestMutation.isPending;
  const status = joined
    ? RequestStatus.ACCEPTED
    : requested
      ? RequestStatus.PENDING
      : RequestStatus.UNDETERMINED;

  const text = {
    [RequestStatus.ACCEPTED]: 'Joined',
    [RequestStatus.PENDING]: 'Requested',
    [RequestStatus.REJECTED]: 'Requested',
    [RequestStatus.UNDETERMINED]: 'Join',
  };

  const icon = {
    [RequestStatus.ACCEPTED]: 'check',
    [RequestStatus.PENDING]: 'clock',
    [RequestStatus.REJECTED]: 'clock',
    [RequestStatus.UNDETERMINED]: 'plus',
  };

  return (
    <Chip
      onPress={onPress}
      icon={chooseByStatus(status, icon)}
      disabled={mutationLoading}
      {...props}>
      {chooseByStatus(status, text)}
    </Chip>
  );
}
